/**
 * KNK-1.53 / FR-43 — importing Doors and knock history from another app's CSV export.
 *
 * The decisions and copy, with no React. The data is migration 20261009a5:
 *   knock_import_overview()                 presets, the Account's Dispositions, past imports
 *   knock_import_preview(...)               a dry run: the real import, rolled back
 *   knock_import_apply(..., knocks, doors)  the import; the counts are the previewed ones
 *   knock_import_revert(id)                 undo the latest import while nothing has happened since
 *   knock_import_save_preset(...)           an Account's own mapping for a source
 *
 * The browser reads the file and applies a preset's column mapping, so the server receives one
 * object per row in a fixed shape (`ImportRow`). Everything that touches data is the server's:
 * rep matching, Door matching, Knocks, Revisits, Do Not Knock, notes and the undo.
 *
 * ⚠️ THIS IS NOT THE GATE. Only `lead.import` + `field.settings.manage`, only while Knock Mode is
 * on; the server refuses everything this screen hides, and re-validates every value it is sent.
 */

export const IMPORT_FIELDS = [
  'address', 'street', 'unit', 'city', 'state', 'zip', 'lat', 'lng',
  'disposition', 'date', 'rep_email', 'rep_name', 'notes', 'dnk', 'come_back_at',
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];

export const FIELD_LABELS: Record<ImportField, string> = {
  address: 'Full address',
  street: 'Street',
  unit: 'Unit or apartment',
  city: 'City',
  state: 'State',
  zip: 'ZIP',
  lat: 'Latitude',
  lng: 'Longitude',
  disposition: 'Disposition (their status)',
  date: 'Date knocked',
  rep_email: 'Rep email',
  rep_name: 'Rep name',
  notes: 'Notes',
  dnk: 'Do Not Knock flag',
  come_back_at: 'Come-back time',
};

/** The header chosen for each field. Absent means "not in this file". */
export type ColumnMap = Partial<Record<ImportField, string>>;
/** Their code → our Disposition code; null = "make the Door, no Knock"; undefined = not chosen yet. */
export type CodeMap = Record<string, string | null | undefined>;

export interface ImportPreset {
  sourceKey: string;
  name: string;
  builtin: boolean;
  columns: Partial<Record<ImportField, string[]>>;
  /** Built-in: their code → a Disposition CATEGORY. Account preset: → a Disposition code, or null. */
  codes: Record<string, string | null>;
}

export interface ImportDisposition {
  code: string;
  name: string;
  category: string;
  active: boolean;
}

export interface ImportBatch {
  id: string;
  appliedAt: string;
  sourceName: string;
  fileName: string | null;
  runByName: string | null;
  rowsTotal: number;
  knocksImported: number;
  doorsCreated: number;
  revertedAt: string | null;
  knocksRemoved: number | null;
  /** The server's sentence for why it cannot be undone, or null when it can. */
  revertBlocker: string | null;
  undoable: boolean;
}

export interface ImportOverview {
  presets: ImportPreset[];
  dispositions: ImportDisposition[];
  batches: ImportBatch[];
  maxRows: number;
}

/** Exactly what `knock_import_run` reads from each element of p_rows. */
export interface ImportRow {
  row: number;
  address: string | null;
  lat: number | null;
  lng: number | null;
  code: string | null;
  at: string | null;
  rep_email: string | null;
  rep_name: string | null;
  note: string | null;
  dnk: boolean;
  come_back_at: string | null;
}

export interface ImportPreview {
  rowsTotal: number;
  knocksImported: number;
  doorsCreated: number;
  doorsMatched: number;
  revisitsCreated: number;
  dnkMarked: number;
  notesAdded: number;
  rowsPlaceholder: number;
  rowsSkipped: number;
  unmappedCodes: string[];
  placeholderReps: string[];
  skipReasons: Record<string, number>;
  skippedRows: { row: number; reason: string }[];
}

export const IMPORT_COPY = {
  title: 'Import from another app',
  hint: 'Bring in Doors and knock history from a SalesRabbit, SPOTIO or other CSV export. You map the columns and their statuses, see a dry run, then import. An import can be undone until someone works one of its Doors.',
  source: 'Exported from',
  file: 'CSV file',
  columnsTitle: 'Columns',
  columnsHint: 'Matched from the file\'s headers. Change any that are wrong.',
  notInFile: 'Not in this file',
  codesTitle: 'Their statuses',
  codesHint: 'Choose the Disposition each of their statuses becomes.',
  skipCode: 'Skip: make the Door, no Knock',
  chooseCode: 'Choose…',
  preview: 'Preview',
  apply: 'Import',
  undo: 'Undo',
  savePreset: 'Save this mapping',
  presetSaved: 'Mapping saved for this account',
  applied: 'Imported',
  undone: 'Import undone',
  batchesTitle: 'Imports',
  noBatches: 'Nothing imported yet.',
  undoHint: 'The latest import can be undone until one of its Doors is knocked, noted, booked or marked since.',
  emptyFile: 'That file has no rows under its header.',
  needAddress: 'Choose the address column, or its street, city, state and ZIP columns.',
  noDisposition: 'No status column: Doors are imported without any knock history.',
  noCoordinates: 'No latitude and longitude: only rows matching a Door already here can be imported, as nothing here looks addresses up.',
  noDate: 'No date column: rows import as Doors without Knocks, since a Knock needs its time.',
  nothingToImport: 'Nothing to import: every row is skipped or already here.',
  placeholderName: 'Imported rep',
} as const;

export const SKIP_REASONS: Record<string, [one: string, many: string]> = {
  no_address: ['has no address', 'have no address'],
  address_not_understood: ['has an address we could not read', 'have addresses we could not read'],
  no_location: ['has no location for a new Door', 'have no location for a new Door'],
  no_disposition: ['has no status', 'have no status'],
  code_unmapped: ['has a status not mapped yet', 'have a status not mapped yet'],
  code_skipped: ['has a status you chose to skip', 'have a status you chose to skip'],
  no_date: ['has no date', 'have no date'],
  date_in_future: ['is dated in the future', 'are dated in the future'],
  already_imported: ['is already here', 'are already here'],
  duplicate_row: ['repeats an earlier row', 'repeat an earlier row'],
};

// ---------------------------------------------------------------------------
// Reading the file
// ---------------------------------------------------------------------------

/**
 * RFC 4180: quoted fields may hold commas, quotes ("") and line breaks; CRLF or LF; a leading BOM.
 * Hand-rolled because csv-parse needs Node's Buffer, which the browser only has after main.tsx's
 * polyfill runs, and this module is imported before that line executes.
 */
export function parseCsvTable(input: string): string[][] {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"' && field === '') {
      quoted = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field); rows.push(row); row = []; field = '';
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  return rows.map((r) => r.map((f) => f.trim()));
}

export function parseCsvText(text: string): { headers: string[]; records: Record<string, string>[] } {
  const [head, ...body] = parseCsvTable(text);
  const records = body.filter((r) => r.some((c) => c !== ''));
  if (!head || records.length === 0) throw new Error(IMPORT_COPY.emptyFile);
  const headers = head.map((h) => h.trim());
  return {
    headers,
    records: records.map((r) => Object.fromEntries(headers.map((h, i) => [h, r[i] ?? '']))),
  };
}

const norm = (s: string) => s.trim().toLowerCase();

/** The preset's aliases against the file's headers. A header serves one field: the first in order. */
export function autoMapColumns(headers: string[], preset: ImportPreset): ColumnMap {
  const byName = new Map(headers.map((h) => [norm(h), h]));
  const used = new Set<string>();
  const map: ColumnMap = {};
  for (const field of IMPORT_FIELDS) {
    for (const alias of preset.columns[field] ?? []) {
      const header = byName.get(norm(alias));
      if (header && !used.has(header)) {
        map[field] = header;
        used.add(header);
        break;
      }
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Dates: US exports are month-first and carry no zone, so they are read as the importer's local time
// ---------------------------------------------------------------------------

const valid = (d: Date, y: number, mo: number, da: number) =>
  !Number.isNaN(d.getTime()) && d.getFullYear() === y && d.getMonth() === mo - 1 && d.getDate() === da;

export function parseImportDate(text: string | null | undefined): string | null {
  const s = (text ?? '').trim();
  if (!s) return null;

  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})(?:[ T]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?$/.exec(s);
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/.exec(s);
  let y: number, mo: number, da: number, h = 0, mi = 0, se = 0;
  if (us) {
    mo = +us[1]; da = +us[2]; y = us[3].length === 2 ? 2000 + +us[3] : +us[3];
    if (us[4] !== undefined) {
      h = +us[4]; mi = +us[5]; se = us[6] ? +us[6] : 0;
      const ampm = us[7]?.toLowerCase();
      if (ampm) {
        if (h < 1 || h > 12) return null;
        h = (h % 12) + (ampm === 'pm' ? 12 : 0);
      }
    }
  } else if (iso) {
    y = +iso[1]; mo = +iso[2]; da = +iso[3];
    if (iso[4] !== undefined) { h = +iso[4]; mi = +iso[5]; se = iso[6] ? +iso[6] : 0; }
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : new Date(t).toISOString();
  } else {
    return null;
  }
  if (h > 23 || mi > 59 || se > 59) return null;
  const d = new Date(y, mo - 1, da, h, mi, se);
  return valid(d, y, mo, da) ? d.toISOString() : null;
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

const TRUE_WORDS = new Set(['y', 'yes', 'true', '1', 'x', 'dnk', 'do not knock']);

const num = (s: string | undefined): number | null => {
  if (s === undefined || s.trim() === '') return null;
  const n = Number(s.trim());
  return Number.isFinite(n) ? n : null;
};
const text = (s: string | undefined): string | null => (s && s.trim() ? s.trim() : null);

function addressOf(rec: Record<string, string>, map: ColumnMap): string | null {
  const v = (f: ImportField) => (map[f] ? text(rec[map[f] as string]) : null);
  const whole = v('address');
  if (whole) return whole;
  const street = [v('street'), v('unit')].filter(Boolean).join(' ');
  const tail = [v('state'), v('zip')].filter(Boolean).join(' ');
  const parts = [street, v('city'), tail].filter((p) => p);
  return street ? parts.join(', ') : null;
}

/** One object per record, in the shape knock_import_run reads. Row 1 is the header, as in a spreadsheet. */
export function buildImportRows(records: Record<string, string>[], map: ColumnMap): ImportRow[] {
  const v = (rec: Record<string, string>, f: ImportField) => (map[f] ? rec[map[f] as string] : undefined);
  return records.map((rec, i) => ({
    row: i + 2,
    address: addressOf(rec, map),
    lat: num(v(rec, 'lat')),
    lng: num(v(rec, 'lng')),
    code: text(v(rec, 'disposition')),
    at: parseImportDate(v(rec, 'date')),
    rep_email: text(v(rec, 'rep_email')),
    rep_name: text(v(rec, 'rep_name')),
    note: text(v(rec, 'notes')),
    dnk: TRUE_WORDS.has(norm(v(rec, 'dnk') ?? '')),
    come_back_at: parseImportDate(v(rec, 'come_back_at')),
  }));
}

export function distinctCodes(rows: Pick<ImportRow, 'code'>[]): { code: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const r of rows) if (r.code) counts.set(r.code, (counts.get(r.code) ?? 0) + 1);
  return [...counts].map(([code, count]) => ({ code, count })).sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
}

export function splitIntoParts<T>(rows: T[], max: number): T[][] {
  const parts: T[][] = [];
  for (let i = 0; i < rows.length; i += max) parts.push(rows.slice(i, i + max));
  return parts;
}

// ---------------------------------------------------------------------------
// Codes
// ---------------------------------------------------------------------------

export function suggestCodeMapping(
  codes: { code: string }[],
  preset: ImportPreset,
  dispositions: ImportDisposition[],
): CodeMap {
  const presetCodes = new Map(Object.entries(preset.codes).map(([k, v]) => [norm(k), v]));
  const byCode = new Map(dispositions.map((d) => [d.code, d]));
  const active = dispositions.filter((d) => d.active);
  const out: CodeMap = {};
  for (const { code } of codes) {
    const hint = presetCodes.get(norm(code));
    if (hint === null) {
      out[code] = null;
    } else if (hint !== undefined && byCode.has(hint)) {
      out[code] = hint;
    } else if (hint !== undefined && active.some((d) => d.category === hint)) {
      out[code] = active.find((d) => d.category === hint)?.code;
    } else {
      out[code] = (active.find((d) => norm(d.name) === norm(code) || d.code === norm(code))
        ?? dispositions.find((d) => norm(d.name) === norm(code) || d.code === norm(code)))?.code;
    }
  }
  return out;
}

/** Undecided codes are left out so the server names them; a skip goes as null. */
export function codesPayload(map: CodeMap): Record<string, string | null> {
  return Object.fromEntries(Object.entries(map).filter(([, v]) => v !== undefined)) as Record<string, string | null>;
}

export function mappingWarnings(map: ColumnMap): string[] {
  if (!map.address && !map.street) return [IMPORT_COPY.needAddress];
  const w: string[] = [];
  if (!map.disposition) w.push(IMPORT_COPY.noDisposition);
  if (!map.lat || !map.lng) w.push(IMPORT_COPY.noCoordinates);
  if (map.disposition && !map.date) w.push(IMPORT_COPY.noDate);
  return w;
}

export function presetToSave(preset: ImportPreset, map: ColumnMap, codes: CodeMap) {
  const columns: Partial<Record<ImportField, string[]>> = {};
  for (const f of IMPORT_FIELDS) {
    const aliases = preset.columns[f] ?? [];
    const chosen = map[f];
    const list = chosen ? [chosen, ...aliases.filter((a) => norm(a) !== norm(chosen))] : aliases;
    if (list.length) columns[f] = list;
  }
  return {
    p_source_key: preset.sourceKey,
    p_name: preset.name,
    p_mapping: { columns, codes: codesPayload(codes) },
  };
}

// ---------------------------------------------------------------------------
// What the server says back
// ---------------------------------------------------------------------------

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const int = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null);
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

function parsePreset(raw: unknown): ImportPreset | null {
  if (!isRecord(raw) || !isRecord(raw.mapping)) return null;
  const sourceKey = str(raw.source_key);
  const name = str(raw.name);
  if (!sourceKey || !name) return null;
  const columns: Partial<Record<ImportField, string[]>> = {};
  const rawColumns = isRecord(raw.mapping.columns) ? raw.mapping.columns : {};
  for (const f of IMPORT_FIELDS) if (Array.isArray(rawColumns[f])) columns[f] = strings(rawColumns[f]);
  const codes: Record<string, string | null> = {};
  if (isRecord(raw.mapping.codes)) {
    for (const [k, v] of Object.entries(raw.mapping.codes)) if (typeof v === 'string' || v === null) codes[k] = v;
  }
  return { sourceKey, name, builtin: raw.builtin === true, columns, codes };
}

export function parseImportBatches(raw: unknown): ImportBatch[] {
  if (!Array.isArray(raw)) return [];
  const out: ImportBatch[] = [];
  for (const b of raw) {
    if (!isRecord(b)) continue;
    const id = str(b.id);
    const appliedAt = str(b.applied_at);
    if (!id || !appliedAt) continue;
    const revertBlocker = str(b.revert_blocker);
    const revertedAt = str(b.reverted_at);
    out.push({
      id,
      appliedAt,
      sourceName: str(b.source_name) ?? 'Import',
      fileName: str(b.file_name),
      runByName: str(b.run_by_name),
      rowsTotal: int(b.rows_total) ?? 0,
      knocksImported: int(b.knocks_imported) ?? 0,
      doorsCreated: int(b.doors_created) ?? 0,
      revertedAt,
      knocksRemoved: int(b.knocks_removed),
      revertBlocker,
      undoable: revertBlocker === null && revertedAt === null,
    });
  }
  return out;
}

export function parseImportOverview(raw: unknown): ImportOverview | null {
  if (!isRecord(raw) || !Array.isArray(raw.presets)) return null;
  const dispositions: ImportDisposition[] = [];
  for (const d of Array.isArray(raw.dispositions) ? raw.dispositions : []) {
    if (!isRecord(d)) continue;
    const code = str(d.code);
    const name = str(d.name);
    const category = str(d.category);
    if (code && name && category) dispositions.push({ code, name, category, active: d.active !== false });
  }
  return {
    presets: raw.presets.map(parsePreset).filter((p): p is ImportPreset => p !== null),
    dispositions,
    batches: parseImportBatches(raw.batches),
    maxRows: int(raw.max_rows) ?? 500,
  };
}

export function parseImportPreview(raw: unknown): ImportPreview | null {
  if (!isRecord(raw)) return null;
  const knocksImported = int(raw.knocks_imported);
  const rowsTotal = int(raw.rows_total);
  if (knocksImported === null || rowsTotal === null) return null;
  const skipReasons: Record<string, number> = {};
  if (isRecord(raw.skip_reasons)) {
    for (const [k, v] of Object.entries(raw.skip_reasons)) if (int(v) !== null) skipReasons[k] = v as number;
  }
  const skippedRows = (Array.isArray(raw.skipped_rows) ? raw.skipped_rows : [])
    .filter(isRecord)
    .map((r) => ({ row: int(r.row) ?? 0, reason: str(r.reason) ?? '' }))
    .filter((r) => r.row > 0);
  return {
    rowsTotal,
    knocksImported,
    doorsCreated: int(raw.doors_created) ?? 0,
    doorsMatched: int(raw.doors_matched) ?? 0,
    revisitsCreated: int(raw.revisits_created) ?? 0,
    dnkMarked: int(raw.dnk_marked) ?? 0,
    notesAdded: int(raw.notes_added) ?? 0,
    rowsPlaceholder: int(raw.rows_placeholder) ?? 0,
    rowsSkipped: int(raw.rows_skipped) ?? 0,
    unmappedCodes: strings(raw.unmapped_codes),
    placeholderReps: strings(raw.placeholder_reps),
    skipReasons,
    skippedRows,
  };
}

export const canApply = (p: ImportPreview): boolean =>
  p.unmappedCodes.length === 0 && (p.knocksImported > 0 || p.doorsCreated > 0);

const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function skipReasonLabel(reason: string, count = 1): string {
  const words = SKIP_REASONS[reason];
  return words ? (count === 1 ? words[0] : words[1]) : reason;
}

/** What importing would do, one sentence per fact, in the order a person checks them. */
export function previewLines(p: ImportPreview): string[] {
  const lines: string[] = [];
  if (p.unmappedCodes.length > 0) {
    lines.push(`Choose a Disposition for ${p.unmappedCodes.map((c) => `"${c}"`).join(', ')} before importing.`);
  }
  if (!canApply(p) && p.unmappedCodes.length === 0) {
    lines.push(IMPORT_COPY.nothingToImport);
  }
  const doors = p.doorsCreated + p.doorsMatched;
  if (p.knocksImported > 0) {
    const one = p.knocksImported === 1;
    lines.push(`${n(p.knocksImported, 'Knock becomes', 'Knocks become')} history on ${n(doors, 'Door', 'Doors')} (${p.doorsCreated} new, ${p.doorsMatched} already here), marked imported and unverified. ${one ? 'It never scores or counts' : 'They never score or count'} toward pay.`);
  } else if (p.doorsCreated > 0) {
    lines.push(`${n(p.doorsCreated, 'new Door is', 'new Doors are')} added, with no knock history.`);
  }
  if (p.revisitsCreated > 0) {
    lines.push(`${n(p.revisitsCreated, 'future Come Back becomes a Revisit', 'future Come Backs become Revisits')}.`);
  }
  if (p.dnkMarked > 0) lines.push(`${n(p.dnkMarked, 'Door is', 'Doors are')} marked Do Not Knock.`);
  if (p.notesAdded > 0) {
    lines.push(`${n(p.notesAdded, 'note is', 'notes are')} added, visible only to the people on each Lead.`);
  }
  if (p.rowsPlaceholder > 0) {
    const shown = p.placeholderReps.slice(0, 5).join(', ');
    const more = p.placeholderReps.length > 5 ? ` and ${p.placeholderReps.length - 5} more` : '';
    lines.push(`${n(p.rowsPlaceholder, 'row has', 'rows have')} no matching rep and ${p.rowsPlaceholder === 1 ? 'is' : 'are'} credited to "${IMPORT_COPY.placeholderName}"${shown ? `: ${shown}${more}` : ''}.`);
  }
  if (p.rowsSkipped > 0) {
    const parts = Object.keys(SKIP_REASONS)
      .filter((k) => (p.skipReasons[k] ?? 0) > 0)
      .map((k) => `${p.skipReasons[k]} ${skipReasonLabel(k, p.skipReasons[k])}`);
    lines.push(`${n(p.rowsSkipped, 'row is', 'rows are')} skipped${parts.length ? `: ${parts.join('; ')}` : ''}.`);
  }
  return lines;
}

export function batchLine(b: ImportBatch): string {
  const parts = [b.sourceName];
  if (b.fileName) parts.push(b.fileName);
  parts.push(`${n(b.knocksImported, 'Knock', 'Knocks')}, ${n(b.doorsCreated, 'new Door', 'new Doors')}`);
  if (b.revertedAt) parts.push(`undone, ${n(b.knocksRemoved ?? 0, 'Knock', 'Knocks')} removed`);
  else if (b.runByName) parts.push(`by ${b.runByName}`);
  return parts.join(' · ');
}
